import React, { useState } from 'react';

const Input = ({ 
  label,
  error,
  helperText,
  icon,
  iconPosition = 'left',
  size = 'md',
  variant = 'default',
  className = '',
  ...props 
}) => {
  const [focused, setFocused] = useState(false);

  const baseStyles = {
    width: '100%',
    fontFamily: 'var(--font-family)',
    fontSize: 'var(--font-size-base)',
    lineHeight: 'var(--line-height-normal)',
    color: 'var(--color-gray-900)',
    backgroundColor: 'var(--bg-primary)',
    border: `1px solid ${error ? 'var(--color-error)' : focused ? 'var(--color-primary)' : 'var(--color-gray-300)'}`,
    borderRadius: 'var(--radius-md)',
    transition: 'all var(--transition-fast)',
    outline: 'none',
    boxShadow: focused ? '0 0 0 3px rgba(0, 122, 255, 0.1)' : 'none'
  };

  // Touch-friendly sizes with minimum 44px for touch devices
  const sizes = {
    sm: {
      padding: 'var(--space-2) var(--space-3)',
      fontSize: 'var(--font-size-sm)',
      minHeight: '36px'
    },
    md: {
      padding: 'var(--space-3) var(--space-4)',
      fontSize: 'var(--font-size-base)',
      minHeight: '44px' // Touch-friendly minimum
    },
    lg: {
      padding: 'var(--space-4) var(--space-5)',
      fontSize: 'var(--font-size-lg)',
      minHeight: '52px'
    }
  };

  const variants = {
    default: {
      ...baseStyles,
      ...sizes[size]
    },
    filled: {
      ...baseStyles,
      ...sizes[size],
      backgroundColor: 'var(--color-gray-50)',
      borderColor: 'transparent',
      '&:focus': {
        backgroundColor: 'var(--bg-primary)',
        borderColor: 'var(--color-primary)'
      }
    }
  };

  const inputStyles = {
    ...variants[variant],
    paddingLeft: icon && iconPosition === 'left' ? 'var(--space-10)' : sizes[size].padding.split(' ')[1],
    paddingRight: icon && iconPosition === 'right' ? 'var(--space-10)' : sizes[size].padding.split(' ')[1],
    ...props.style
  };

  const iconElement = icon && (
    <span style={{
      position: 'absolute',
      [iconPosition]: 'var(--space-3)',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      color: focused ? 'var(--color-primary)' : 'var(--color-gray-400)',
      fontSize: size === 'sm' ? '14px' : size === 'lg' ? '18px' : '16px',
      transition: 'color var(--transition-fast)'
    }}>
      {icon}
    </span>
  );

  return (
    <div className={className} style={{ width: '100%' }}>
      {label && (
        <label style={{
          display: 'block',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--color-gray-700)',
          marginBottom: 'var(--space-2)'
        }}>
          {label}
        </label>
      )}
      
      <div style={{ position: 'relative', width: '100%' }}>
        {iconElement}
        <input
          {...props}
          style={inputStyles}
          onFocus={(e) => {
            setFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            props.onBlur?.(e);
          }}
        />
      </div>
      
      {(error || helperText) && (
        <div style={{
          marginTop: 'var(--space-1)',
          fontSize: 'var(--font-size-sm)',
          color: error ? 'var(--color-error)' : 'var(--color-gray-500)'
        }}>
          {error || helperText}
        </div>
      )}
    </div>
  );
};

export default Input;
